import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Download, Landmark } from "lucide-react";
import { formatCents } from "@shared/money";
import { addDays, fmtDate, monthEnd, monthOf, monthStart, shiftMonth, todayIn } from "@web/lib/format";
import { accountingCsvUrl, accountingQuery, venuesQuery, type AccountingRow } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { errorMessage } from "@web/lib/api";
import { useBatchValidation } from "@web/lib/validation";
import type { ValidationEvaluation } from "@shared/validation/issues";
import { Button, EmptyState, Inline, Page, Picker, Row, RowGroup, SegmentedControl, Skeleton, Stack, Stat, StatRow, Text, TextField, Toolbar, Workspace, Region, ValidationBadge, ValidationSummary } from "@web/ui";

const PERIODS = [
  { key: "month", label: "Month" },
  { key: "last", label: "Last mo." },
  { key: "year", label: "Year" },
  { key: "custom", label: "Custom" },
] as const;
type Period = (typeof PERIODS)[number]["key"];
export type AccountingSearch = { period?: Period; from?: string; to?: string; venue?: string };

/** The shareable address contract: unknown keys and malformed dates are dropped,
 * and an absent key means the screen's own default. The CSV and the address are
 * then built from the same state, so the link is the view. */
export function parseAccountingSearch(raw: Record<string, unknown>): AccountingSearch {
  const out: AccountingSearch = {};
  if (PERIODS.some((p) => p.key === raw.period)) out.period = raw.period as Period;
  for (const key of ["from", "to"] as const) if (typeof raw[key] === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw[key])) out[key] = raw[key];
  if (typeof raw.venue === "string" && raw.venue && raw.venue.length <= 200) out.venue = raw.venue;
  return out;
}

/** Accounting (SPEC §6.1 item 12, Phase 1): period + venue filters, three numbers, overdue / upcoming / Unknown lists, CSV. */
export function Accounting() {
  const me = useMe();
  const navigate = useNavigate();
  const today = todayIn(me.organization.timezone);
  // Period, custom dates and venue live in the URL, so the address is the view
  // the CSV downloads (docs/plans/RESPONSIVE-STANDARD.md).
  const search = useSearch({ from: "/app/more/accounting" });
  const period = search.period ?? "month";
  const from = search.from ?? addDays(today, -30);
  const to = search.to ?? today;
  const venueId = search.venue ?? "";
  const setSearch = (next: AccountingSearch) => navigate({ to: "/more/accounting", search: { ...search, ...next }, replace: true });
  const venues = useQuery(venuesQuery());
  const range = useMemo(() => {
    const m = monthOf(today);
    if (period === "month") return { from: monthStart(m), to: monthEnd(m) };
    if (period === "last") return { from: monthStart(shiftMonth(m, -1)), to: monthEnd(shiftMonth(m, -1)) };
    if (period === "year") return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
    return { from, to };
  }, [period, from, to, today]);
  const params = { ...range, venueId: venueId || undefined };
  const q = useQuery(accountingQuery(params));
  const a = q.data;
  const checks = useBatchValidation(a ? [...a.overdue, ...a.upcomingDue, ...a.unknown].map(row => ({ entityType: "booking", id: row.id })) : []);
  const rowChecks = { validation: (id: string) => checks.data?.records.find(record => record.entity.type === "booking" && record.entity.id === id), unavailable: checks.isError };
  const open = (b: AccountingRow) => navigate({ to: "/bookings/$id", params: { id: b.id } });

  return (
    <Page>
      <Stack gap={4}>
        <Toolbar>
        {/* Switching to Custom writes the dates it starts from, so the address a
            person copies is the range they are looking at, not the reader's own. */}
        <SegmentedControl label="Period" items={PERIODS} value={period} onChange={(value) => setSearch(value === "custom" ? { period: value, from, to } : { period: value })} />
        {period === "custom" && (
          <Inline gap={2}>
            <TextDate label="From" value={from} onChange={(value) => setSearch({ from: value || undefined })} />
            <TextDate label="To" value={to} onChange={(value) => setSearch({ to: value || undefined })} />
          </Inline>
        )}
        {venues.data && venues.data.length > 1 && <Picker label="Venue" placeholder="All venues" value={venueId} onChange={(value) => setSearch({ venue: value || undefined })} options={venues.data.map((v) => ({ value: v.id, label: v.name }))} />}
        </Toolbar>
        <Text variant="caption" tone="tertiary">
          {fmtDate(range.from)} – {fmtDate(range.to)} · collected by known payment date. Outstanding includes completed balances across all dates, excluding unaccepted quotes. Migrated receipt dates are unknown and excluded from period collections.
        </Text>
        {q.isError ? (
          <EmptyState title="Unable to load accounting" body={errorMessage(q.error)} action={<Button variant="secondary" onClick={() => q.refetch()}>Try again</Button>} />
        ) : !a ? (
          <Skeleton variant="cards" count={3} />
        ) : (
          <>
            <StatRow>
              <Stat label="Collected" cents={a.collected} />
              <Stat label="Outstanding" cents={a.outstanding} tone={a.outstanding > 0 ? "danger" : "default"} />
              <Stat label="Deposits held" cents={a.depositsHeld} />
            </StatRow>
            {a.undatedLegacyReceipts !== 0 && <Text variant="caption" tone="secondary">Migrated receipts with unknown payment dates: {formatCents(a.undatedLegacyReceipts)} across all dates. These are not included in period collections.</Text>}
            {(checks.isError || checks.data?.checksComplete === false) && <ValidationSummary result={checks.data} unavailable onRetry={() => void checks.refetch()} />}
            <Workspace variant="report">
              <Region area="main"><Section title="Overdue" rows={a.overdue} empty="Nothing overdue" onOpen={open} tone="danger" {...rowChecks} /></Region>
              <Region area="aside"><Section title="Upcoming due (30 days)" rows={a.upcomingDue} empty="Nothing due in the next 30 days" onOpen={open} {...rowChecks} /></Region>
              <Region area="header"><Section title="Unknown — confirm" rows={a.unknown} empty="No migrated bookings with an unknown payment status" onOpen={open} hint="Ledger rows whose payment status was Unknown; excluded from Outstanding until a payment is recorded." {...rowChecks} /></Region>
            </Workspace>
            <Button variant="secondary" icon={<Download aria-hidden size={18} />} onClick={() => window.open(accountingCsvUrl(params), "_blank", "noopener")}>
              Download CSV
            </Button>
          </>
        )}
      </Stack>
    </Page>
  );
}

function TextDate({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return <TextField label={label} type="date" className="flex-1" value={value} onChange={(e) => onChange(e.target.value)} />;
}

function Section({ title, rows, empty, onOpen, tone, hint, validation, unavailable }: { title: string; rows: AccountingRow[]; empty: string; onOpen: (r: AccountingRow) => void; tone?: "danger"; hint?: string; validation: (id: string) => ValidationEvaluation | undefined; unavailable: boolean }) {
  return (
    <Stack gap={2}>
      <Inline justify="between">
        <Text variant="headline">{title}</Text>
        <Text as="span" variant="callout" tone={tone ?? "secondary"} tabular>
          {formatCents(rows.reduce((s, r) => s + r.balanceDue, 0))}
        </Text>
      </Inline>
      {hint && (
        <Text variant="caption" tone="tertiary">
          {hint}
        </Text>
      )}
      {rows.length === 0 ? (
        <EmptyState icon={Landmark} title={empty} />
      ) : (
        <RowGroup>
          {rows.map((r) => (
            <Row key={r.id} wrap title={r.clientName || r.eventName || "Booking"} subtitle={`${r.balanceDueDate ? `Due ${fmtDate(r.balanceDueDate)} · ` : "Due date unknown · "}${r.venueName ?? ""} · Event ${fmtDate(r.eventDate)}${r.legacyPaymentStatus ? ` · ledger: ${r.legacyPaymentStatus}` : ""}`} trailing={<Stack gap={1} align="end"><Text as="span" variant="body" tabular tone={tone}>{formatCents(r.balanceDue)}</Text><ValidationBadge result={validation(r.id)} unavailable={unavailable} /></Stack>} onClick={() => onOpen(r)} />
          ))}
        </RowGroup>
      )}
    </Stack>
  );
}
